import {
  DotsThreeVertical,
  Info,
  PencilLine,
  Percent,
  Plus,
} from "@phosphor-icons/react"
import { Circle } from "@phosphor-icons/react/dist/ssr"
import {
  Button,
  Checkbox,
  FormInput,
  Menu,
  MenuItem,
  Note,
  Popover,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { useEffect, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { ReactComponent as EmptyListing } from "~/assets/EmptyListing.svg"
import { ReactComponent as Pdf } from "~/assets/pdf.svg"
import { AddBalanceSheet } from "~/components/CreditManagement/Assessment/AddBalanceSheet"
import { calculatePercentage } from "~/utils/percentage"

export type FinancialAssessmentType = {
  form?: any
  setValues?: any
  isDisabled?: boolean
  isSubmitted?: boolean
}

export const FinancialAssessment = ({
  form,
  isDisabled = false,
  isSubmitted = false,
}: FinancialAssessmentType) => {
  const { getInputProps } = form
  const { id } = useParams()
  const isNew = id === "new"
  const [searchParams, setSearchParams] = useSearchParams()

  const { values: state } = form
  const [isEditing, setEditing] = useState(isNew ? true : false)

  const [selected, setSelected] = useState()

  useEffect(() => {
    setSelected(state?.financial_year_assessments?.[0]?.id)
  }, [])
  const COLUMNS = [
    {
      id: "selector",
      Header: "Select Financial year of assessment",
      accessor: "select_year",
      className: "w-[150px]",
      Cell({ value, row }) {
        const id = row?.original?.id || row?.original?._id
        return (
          <Checkbox
            type="radio"
            checked={selected === id ? true : false}
            onChange={() => {
              setSelected(id)
            }}
          />
        )
      },
    },

    {
      Header: "Financial Year",
      accessor: "financial_year",
      id: "year",
      Cell({ value }) {
        return <Cell title={value?.name} />
      },
    },

    {
      Header: "Capital",
      accessor: "capital",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },

    {
      Header: "Fixed Assets",
      accessor: "fixed_assets",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },
    {
      Header: "Current Liabilities",
      accessor: "current_liabilities",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },
    {
      Header: "Current Assets",
      accessor: "current_assets",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },
    {
      Header: "Profit / Loss",
      accessor: "profit_loss",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },
    {
      Header: "Total Sales",
      accessor: "total_sales",
      Cell({ value }) {
        return <Cell title={inr(value)} />
      },
    },
    {
      Header: "",
      id: "pdf",
      accessor: "attachments",
      Cell({ value }) {
        const att = value?.[0]?.url
        if (!att) return ""
        return (
          <Cell
            title={
              <Link to={value?.[0]?.url} target="_blank">
                <Pdf />
              </Link>
            }
          />
        )
      },
    },

    {
      Header: "",
      accessor: "updated_at",
      id: "action",
      Cell({ row: { original } }) {
        const id = original?.id || original?._id
        return (
          <Popover
            minimal
            arrow={false}
            className="p-0"
            trigger="click"
            interactive
            placement="top"
            content={
              <Menu className="max-h-72">
                <MenuItem
                  onClick={() => {
                    searchParams.set("balance_sheet", id)
                    setSearchParams(searchParams)
                  }}
                >
                  Edit
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded" hidden={isDisabled}>
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  const totalSales = state?.financial_year_assessments?.find(bal => {
    const id = bal?.id || bal?._id
    return id === selected
  })?.total_sales
  const creditLimit = state?.credit_limit_factor
  const assessedLimit = calculatePercentage({
    percentage: creditLimit,
    total: totalSales,
  })

  return (
    <>
      <header className="text-lg text-green font-500 bg-gray-200 p-3 border-y-1 border-gray-300">
        Financial Assessment
      </header>

      <section id="credit_limit">
        <header className="flex gap-1 items-center text-green border-b-1 border-gray-300 p-3">
          <Circle weight="fill" className="text-green" />
          Credit Limit Factor
        </header>
        <div className="py-3 flex gap-4 border-b-1 border-gray-300 p-3">
          <FormInput
            disabled={!isEditing}
            label="Credit Limit"
            inline
            labelProps={{
              className: "text-gray-600",
            }}
            size="sm"
            rightElement={<Percent className="mr-2" />}
            {...getInputProps("credit_limit_factor")}
            error={
              isSubmitted && !state?.credit_limit_factor
                ? "Please Enter Credit Limit Factor"
                : ""
            }
          />
          <Button
            hidden={isNew || isDisabled}
            intent="info"
            variant="ghost"
            text={isEditing ? "Save" : "Edit"}
            leftIcon={!isEditing ? <PencilLine /> : <Plus />}
            onClick={() => {
              setEditing(!isEditing)
            }}
            size="sm"
          />
        </div>
      </section>

      <section id="credit_limit">
        <header className="border-b-1 border-gray-300 p-3 flex justify-between">
          <div className="flex gap-1 items-center text-green ">
            <Circle weight="fill" className="text-green" />
            Financial Year Balance Sheet Details
          </div>
          <div>
            <Button
              size="sm"
              text="Bal. Sheet"
              leftIcon={<Plus />}
              variant="outline"
              onClick={() => {
                searchParams.set("balance_sheet", "true")
                setSearchParams(searchParams)
              }}
              disabled={isDisabled}
            />
          </div>
        </header>
        <div className="py-3  border-b-1 border-gray-300 ">
          <TableView
            data={state?.financial_year_assessments || []}
            tableClassName="!m-0 !shadow-none"
            columns={COLUMNS}
            noDataProps={{
              image: <EmptyListing />,
              title: "Empty Listing",
              className: "min-h-[20rem] !p-0",
            }}
            getHeaderProps={h => {
              return { className: "!z-0" }
            }}
          />
        </div>
      </section>
      <section
        id={"assessed_financial_limit"}
        className={classNames(
          "flex flex-col gap-3 m-4",
          assessedLimit ? "" : "hidden"
        )}
      >
        <div className="flex gap-2">
          <p>Assessed Limit : </p>
          <p className="font-700">{inr(assessedLimit)}</p>
        </div>
        <Note className="border-l-4 border-blue" intent="info">
          <div className="flex gap-2 text-blue-300 font-400 text-[14px]">
            <Info size={20} weight="fill" className="mt-1" />
            <p>
              "Upon selecting a financial year, the system auto calculates the
              relevant data points and displays the result under the Assessed
              Limit.
              <br />
              The calculation is based on the formula:{" "}
              <strong>
                Assessed Limit = Total Sales × Credit Limit Percentage.
              </strong>
              "
            </p>
          </div>
        </Note>
      </section>
      <AddBalanceSheet form={form} />
    </>
  )
}
